import { useEffect, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Check, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { CustomerSupervisorSelect, ProjectManagerDropdown } from "@/components/ProjectAssignmentControls";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export function ManagerWorkflowDialog({
  canAccessBilling,
  companyLabels,
  onOpenChange,
  onSaved,
  project,
}) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (project)
      setForm({
        client: project.customer || "",
        customer_supervisor: project.customer_supervisor || "",
        due_date: project.due_date || "",
        notes: project.notes || "",
        project_manager_user_ids: project.project_manager_user_ids || [],
        remaining_work: project.remaining_work || "",
        scheduled_date: project.next_scheduled_date || "",
        what_is_invoiced: project.what_is_invoiced || "",
      });
  }, [project]);
  useEffect(() => {
    if (!project) return;
    axios
      .get(`${API}/assignable-users`)
      .then((response) => setUsers(response.data.users || []))
      .catch(() => setUsers([]));
  }, [project]);
  const setValue = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const saveChanges = async (event) => {
    event.preventDefault();
    if (!project || !form) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/projects/${project.id}`, {
        client: form.client,
        customer_supervisor: form.customer_supervisor,
        due_date: form.due_date,
        notes: form.notes,
        project_manager_user_ids: form.project_manager_user_ids,
        scheduled_date: form.scheduled_date,
        ...(canAccessBilling
          ? { what_is_invoiced: form.what_is_invoiced }
          : {}),
      });
      if (form.remaining_work !== (project.remaining_work || ""))
        await axios.post(`${API}/projects/${project.id}/remaining-work`, {
          description: form.remaining_work,
        });
      toast.success("Manager workflow details saved.");
      onOpenChange(false);
      onSaved();
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.detail ||
          "Could not save workflow details.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={Boolean(project)}>
      <DialogContent
        className="manager-edit-dialog"
        data-testid="manager-workflow-dialog"
      >
        <DialogHeader>
          <p className="operations-kicker">Manager workflow details</p>
          <DialogTitle data-testid="manager-workflow-dialog-title">
            {project?.project_number}
          </DialogTitle>
          <DialogDescription>
            Project Stage is calculated by Operations and cannot be changed
            here.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <form
            className="manager-workflow-form"
            data-testid="manager-workflow-form"
            onSubmit={saveChanges}
          >
            <Field
              label="Customer"
              testId="workflow-customer-input"
              value={form.client}
              onChange={(value) => setValue("client", value)}
            />
            <label>
              Customer Supervisor
              <CustomerSupervisorSelect
                onChange={(value) => setValue("customer_supervisor", value)}
                testId="workflow-customer-supervisor-input"
                value={form.customer_supervisor}
              />
            </label>
            <label>
              Assigned {companyLabels.projectManager}s
              <ProjectManagerDropdown
                onChange={(value) => setValue("project_manager_user_ids", value)}
                testId="workflow-project-manager"
                users={users}
                value={form.project_manager_user_ids || []}
              />
            </label>
            <DateField
              label="Due Date"
              testId="workflow-due-date-input"
              value={form.due_date}
              onChange={(value) => setValue("due_date", value)}
            />
            <DateField
              label="Scheduled Date"
              testId="workflow-scheduled-date-input"
              value={form.scheduled_date}
              onChange={(value) => setValue("scheduled_date", value)}
            />
            <TextField
              label="What Is Left of Work"
              testId="workflow-remaining-work-input"
              value={form.remaining_work}
              onChange={(value) => setValue("remaining_work", value)}
            />
            {canAccessBilling && (
              <TextField
                label="What Is Invoiced"
                testId="workflow-invoiced-input"
                value={form.what_is_invoiced}
                onChange={(value) => setValue("what_is_invoiced", value)}
              />
            )}
            <TextField
              label="Notes"
              testId="workflow-notes-input"
              value={form.notes}
              onChange={(value) => setValue("notes", value)}
            />
            <DialogFooter className="manager-workflow-form-wide">
              <Button
                data-testid="manager-workflow-save-button"
                disabled={saving}
                type="submit"
              >
                <Check size={15} />
                {saving ? "Saving…" : "Save workflow details"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, onChange, testId, type = "text", value }) {
  return (
    <label>
      {label}
      <Input
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        value={value}
      />
    </label>
  );
}
function TextField({ label, onChange, testId, value }) {
  return (
    <label className="manager-workflow-form-wide">
      {label}
      <Textarea
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      />
    </label>
  );
}

function DateField({ label, onChange, testId, value }) {
  const selectedDate = value ? parseISO(value) : undefined;
  return (
    <label className="manager-date-field">
      {label}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="manager-date-trigger"
            data-testid={testId}
            type="button"
          >
            <CalendarDays size={15} />
            <span>
              {selectedDate
                ? format(selectedDate, "MMM d, yyyy")
                : "Select date"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="manager-date-popover">
          <Calendar
            mode="single"
            onSelect={(date) =>
              onChange(date ? format(date, "yyyy-MM-dd") : "")
            }
            selected={selectedDate}
          />
          {value && (
            <button
              className="manager-date-clear"
              data-testid={`${testId}-clear-button`}
              onClick={() => onChange("")}
              type="button"
            >
              Clear date
            </button>
          )}
        </PopoverContent>
      </Popover>
    </label>
  );
}
